import { generateClient } from 'aws-amplify/api';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition';
import { GenericRepository } from '@/repository/GenericRepository';
import {
  FilterRowSelection,
  FilterSelections,
} from '@lambda-layer/types/filter';
import { ExclusionRule, SearchableAggregateBucketResultItem } from '@/API';
import { CountMapUtils } from '@/utils/CountMapUtils';
import { buildSearchAggregateQuery } from '@lambda-layer/utils/queryUtils';

export abstract class ListRepository<T> extends GenericRepository<T> {
  protected client = generateClient();

  async aggregate(
    dbColumn: string,
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions: ExclusionRule[] = []
  ): Promise<Map<string | null, number>> {
    const { buckets, total } =
      await this.getSearchableAggregateBucketResultItems(
        dbColumn,
        filterDefinitions,
        filterSelections,
        exclusions
      );

    // Create a Map of key to doc_count
    const bucketMap = new Map<string | null, number>(
      buckets
        // Filter out any buckets that are missing a key or doc_count.
        .filter(bucket => bucket && bucket.doc_count !== undefined)
        // Map the buckets to a tuple of key and doc_count. For example, { key: 'zipCodes', doc_count: 2 } becomes ['zipCodes', 2].
        .map(bucket => [bucket?.key, bucket?.doc_count]) as [string, number][]
    );

    // Sum the counts from buckets
    const sumOfBucketCounts = buckets.reduce((sum, bucket) => {
      return sum + (bucket?.doc_count ?? 0);
    }, 0);

    // Calculate the number of null / undefined values
    const nullCount = total - sumOfBucketCounts;

    // Include the null count if it's greater than zero
    if (nullCount > 0) {
      bucketMap.set(null, nullCount);
    }
    // Include total matching record count under total count key
    CountMapUtils.setTotalCount(bucketMap, total);

    return bucketMap;
  }

  abstract count(
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions?: ExclusionRule[]
  ): Promise<number>;

  protected abstract getSearchableAggregateBucketResultItems(
    dbColumn: string,
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions?: ExclusionRule[]
  ): Promise<{
    buckets: (SearchableAggregateBucketResultItem | null)[];
    total: number;
  }>;

  protected createAggregateQuery(
    dbColumn: string,
    queryName: string,
    filterFields: FilterDefinition[],
    filterSelections: Record<string, FilterRowSelection>,
    exclusions: ExclusionRule[] = [],
    nextToken?: string
  ): string {
    return buildSearchAggregateQuery(
      dbColumn,
      queryName,
      filterFields,
      filterSelections,
      exclusions,
      nextToken
    );
  }
}
